import { Box, Grid, Typography } from "@mui/material";
import React, { useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import {
  useDetector,
  useEquipment,
  useUser,
} from "@services/servicesGenericsTables";
import { useLaboratory } from "@services/servicesGenericsTables/useLaboratory";
import useAnalysis from "@app/pages/samples/utils/useAnalysis";
import { useTypesUnits } from "@services/servicesGenericsTables/useTypesUnits";
import useFilteredProcedures from "@app/pages/samples/hook/useFilteredProcedures";
import {
  MAutocomplete,
  MButton,
  MDatePicker,
  MTextField,
  ResponsiveFormContainer,
} from "@app/components";
import ResultsTable from "../commonForms/resultsTable";
import dayjs from "dayjs";
import { convertToNumber } from "@app/utils/functions";
import { PROCESS_OPTIONS_VALUE } from "@app/pages/background/utils/constans";
import { useBackgroundCodes } from "@services/servicesGenericsTables/useBackgroundCodes";

const CalculationBPP = () => {
  const form = useFormContext();
  const { userList } = useUser();
  const { equipmentList } = useEquipment();
  const { laboratoriesList } = useLaboratory();
  const { currentCalculation, saveCalculate, loadingResults } = useAnalysis();
  const { typesUnitsis } = useTypesUnits();
  const { detectorsList } = useDetector();
  const filteredCalculation = useFilteredProcedures("CAL");
  const codeAnalysis = form.getValues("typeAnalysis")?.codigo;
  const { backgroundCodesList } = useBackgroundCodes();

  const handleCalculate = async () => {
    await saveCalculate(codeAnalysis as string);
  };

  const textButtonCalculation = "Calcular";

  const calcularDesviacionEstandar = (valores) => {
    const valoresFiltrados = valores.filter(
      (val) => val !== null && val !== undefined && val !== ""
    );
    if (valoresFiltrados.length < 2) return 0; // STDEV.S requiere al menos 2 valores
    const media =
      valoresFiltrados.reduce((a, b) => a + b, 0) / valoresFiltrados.length;
    const varianza =
      valoresFiltrados.reduce(
        (sum, valor) => sum + Math.pow(valor - media, 2),
        0
      ) /
      (valoresFiltrados.length - 1);
    return Math.sqrt(varianza);
  };

  const lectura1 = convertToNumber(form.watch("lectura_1_usv")) || 0;
  const lectura2 = convertToNumber(form.watch("lectura_2_usv")) || 0;
  const lectura3 = convertToNumber(form.watch("lectura_3_usv")) || 0;
  const lectura4 = convertToNumber(form.watch("lectura_4_usv")) || 0;

  const valorMediaLecturas = useMemo(() => {
    const lecturas = [lectura1, lectura2, lectura3, lectura4].filter(
      (val) => val !== 0
    );
    if (lecturas.length === 0) return 0;
    return (lecturas.reduce((a, b) => a + b, 0) / lecturas.length).toFixed(1);
  }, [lectura1, lectura2, lectura3, lectura4]);

  const errorLecturas = useMemo(() => {
    const lecturas = [lectura1, lectura2, lectura3, lectura4].filter(
      (val) => val !== 0
    );
    return calcularDesviacionEstandar(lecturas).toFixed(1);
  }, [lectura1, lectura2, lectura3, lectura4]);

  const startSamplingDate = form.watch("start_date_time");
  const endSamplingDate = form.watch("end_date_time");
  const deleteDate = form.watch("calculation_delete_date");
  const readDate = form.watch("calculation_read_date");

  const transitDays = useMemo(() => {
    const inicio = dayjs(startSamplingDate).startOf("day");
    const borrado = dayjs(deleteDate).startOf("day");
    const lectura = dayjs(readDate).startOf("day");
    const fin = dayjs(endSamplingDate).startOf("day");
    const dias1 = inicio.diff(borrado, "day");
    const dias2 = lectura.diff(fin, "day");

    return dias1 + dias2;
  }, [startSamplingDate, endSamplingDate, deleteDate, readDate]);

  const expositionDays = useMemo(() => {
    const inicio = dayjs(startSamplingDate).startOf("day");
    const fin = dayjs(endSamplingDate).startOf("day");
    return fin.diff(inicio, "day");
  }, [startSamplingDate, endSamplingDate]);

  useEffect(() => {
    form.setValue("valor_media_lecturas_usv", valorMediaLecturas);
    form.setValue("error_lecturas_usv", errorLecturas);
    form.setValue("transit_days", transitDays);
    form.setValue("exposition_days", expositionDays);
  }, [valorMediaLecturas, errorLecturas, transitDays, expositionDays, form]);

  return (
    <>
      {currentCalculation.index !== undefined && (
        <>
          <ResponsiveFormContainer columns={3}>
            <MDatePicker
              key="start_date_time"
              name="start_date_time"
              pickerType="datetime"
              label="Fecha y hora de inicio Toma de muestra"
              disabled
            />
            <MDatePicker
              key="end_date_time"
              name="end_date_time"
              pickerType="datetime"
              label="Fecha y hora fin Toma de muestra"
              disabled
            />
          </ResponsiveFormContainer>

          <Grid item xs={12} my={2}>
            <Typography variant="tagText">
              {"Datos ficha del procesamiento, preparación y contaje:"}
            </Typography>
          </Grid>

          <ResponsiveFormContainer columns={3}>
            <MTextField
              key={JSON.stringify(form.getValues("amount_sample"))}
              label="Cantidad de muestra"
              name="calculation_amount_sample"
              type="text"
              fullWidth
              disabled
            />
            <MAutocomplete
              key={JSON.stringify(form.getValues("calculation_units"))}
              name="calculation_units"
              options={typesUnitsis}
              label="Unidades"
              getOptionLabel={(option) =>
                `${option.codigo} | ${option.descripcion}`
              }
              disabled
            />
            <MTextField
              key={JSON.stringify(form.getValues("calculation_tsie_sample"))}
              name="calculation_tsie_sample"
              label="Peso Húmedo/ Seco"
              type="text"
              disabled
              fullWidth
            />
            <MTextField
              key={JSON.stringify(form.getValues("calculation_shared"))}
              name="calculation_shared"
              label="Compartida"
              type="text"
              disabled
              fullWidth
            />
            <MAutocomplete
              key={JSON.stringify(form.getValues("calculation_equipment"))}
              name="calculation_equipment"
              options={equipmentList}
              label="Equipo de Contaje"
              getOptionLabel={(option) =>
                `${option.codigo} - ${option.descripcion}` || ""
              }
              disabled
              multiple={true}
            />
            <MAutocomplete
              key={JSON.stringify(form.getValues("calculation_detector"))}
              name="calculation_detector"
              options={detectorsList}
              label="Detector"
              getOptionLabel={(option) => `${option.name}` || ""}
              disabled
              multiple
            />
            <MDatePicker
              key="counting_date"
              name="counting_date"
              pickerType="date"
              label="Fecha de contaje"
              validation={{
                required: "La fecha de contaje es requerida",
              }}
              disabled
            />
            <MTextField
              name="first_counting_time"
              placeholder="Tiempo de contaje (min)"
              label="Tiempo de contaje (min)"
              type="number"
              validation={{ required: "El tiempo total es requerido" }}
              fullWidth
              step="0.01"
              disabled
            />
            <MTextField
              name="first_counting_cpm"
              placeholder="cpm alfa brutas de la muestra"
              label="cpm alfa brutas de la muestra"
              type="number"
              validation={{ required: "El cpm es requerido" }}
              fullWidth
              step="0.01"
              disabled
            />
            <MTextField
              name="second_counting_cpm"
              placeholder="cpm beta brutas de la muestra"
              label="cpm beta brutas de la muestra"
              type="number"
              validation={{
                required: "El cpm beta brutas de la muestra es requerido",
              }}
              fullWidth
              step="0.01"
              disabled
            />
          </ResponsiveFormContainer>

          <Grid item xs={12} my={2}>
            <Typography variant="tagText">{"Datos a cumplimentar:"}</Typography>
          </Grid>

          <ResponsiveFormContainer columns={4}>
            <MAutocomplete
              name="analyst"
              options={userList}
              label="Seleccione un analista"
              getOptionLabel={(option) => option.username || ""}
              validation={{ required: "El analista es requerido" }}
            />
            <MAutocomplete
              key={JSON.stringify(form.getValues("laboratory"))}
              name="laboratory"
              options={laboratoriesList}
              label="Seleccione un Laboratorio"
              getOptionLabel={(option) =>
                `${option.codigo} - ${option.descripcion}` || ""
              }
              validation={{ required: "El laboratorio es requerido" }}
            />

            <MAutocomplete
              key={JSON.stringify(form.getValues("calculation_procedure"))}
              name="calculation_procedure"
              options={filteredCalculation}
              label="Procedimiento MLPMA"
              getOptionLabel={(option) => `${option.numop}` || ""}
            />

            <MAutocomplete
              key={JSON.stringify(form.getValues("calculation_fondo"))}
              name="calculation_fondo"
              options={backgroundCodesList.filter(
                (fondo) => fondo.proceso === PROCESS_OPTIONS_VALUE.BETA
              )}
              label="Fondo Beta"
              getOptionLabel={(option) =>
                `${option.proceso} - ${option.tipo_codigo}${
                  option.medio_filtro ? ` - ${option.medio_filtro}` : ""
                }` || ""
              }
              validation={{ required: "El fondo es requerido" }}
            />
            <MAutocomplete
              key={JSON.stringify(form.getValues("fondo_alfa"))}
              name="fondo_alfa"
              options={backgroundCodesList.filter(
                (fondo) => fondo.proceso === PROCESS_OPTIONS_VALUE.ALFA
              )}
              label="Fondo Alfa"
              getOptionLabel={(option) =>
                `${option.proceso} - ${option.tipo_codigo}${
                  option.medio_filtro ? ` - ${option.medio_filtro}` : ""
                }` || ""
              }
              validation={{ required: "El fondo es requerido" }}
            />
            <MDatePicker
              name="calculation_date"
              pickerType="datetime"
              label="Fecha Cálculo"
            />
          </ResponsiveFormContainer>

          {currentCalculation.id !== undefined && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mt: 2,
              }}
            >
              <MButton
                onClick={handleCalculate}
                text={textButtonCalculation}
                color="secondary"
                loading={loadingResults}
                disabled={!!form.watch("idResultPreload")}
              />
            </Box>
          )}

          <ResultsTable />
        </>
      )}
    </>
  );
};

export default CalculationBPP;
